import { type MutableRefObject, useRef } from 'react';
import type { ConversationTurn } from '../types';

export interface FinalizeTranscriptPayload {
  type: 'session.finalize';
  conversation_turns: ConversationTurn[];
  start_time: string | null;
}

export interface RestoreTranscriptPayload {
  type: 'session.restore_transcript';
  conversation_turns: ConversationTurn[];
  start_time: string | null;
}

export interface UseConversationTranscriptResult {
  /** Canonical, frontend-owned transcript (source of truth across backend pods). */
  conversationTurnsRef: MutableRefObject<ConversationTurn[]>;
  /** ISO start time of the session, set on first successful connection. */
  sessionStartTimeRef: MutableRefObject<string | null>;
  /** Whether any turns have been recorded. */
  hasTurns: () => boolean;
  /** Number of recorded turns. */
  turnCount: () => number;
  /** Set the session start time to now if it has not been set yet. */
  ensureStartTime: () => void;
  /** Append a turn to the canonical transcript. */
  appendTurn: (role: 'user' | 'assistant', content: string) => void;
  /** Mark the most recent assistant turn as interrupted (user barge-in). */
  markLastAssistantInterrupted: () => void;
  /** Build the `session.finalize` message payload. */
  buildFinalizePayload: () => FinalizeTranscriptPayload;
  /** Build the `session.restore_transcript` message payload. */
  buildRestorePayload: () => RestoreTranscriptPayload;
  /** Clear the transcript and start time (new session). */
  reset: () => void;
}

/**
 * Owns the frontend's canonical conversation transcript and session start time
 * (the source of truth that survives backend pod hand-offs across reconnects).
 * Exposes append/mutate helpers and message-payload builders so the connection
 * machine doesn't manipulate the raw arrays directly.
 */
export const useConversationTranscript = (): UseConversationTranscriptResult => {
  const conversationTurnsRef = useRef<ConversationTurn[]>([]);
  const sessionStartTimeRef = useRef<string | null>(null);

  const hasTurns = () => conversationTurnsRef.current.length > 0;
  const turnCount = () => conversationTurnsRef.current.length;

  const ensureStartTime = () => {
    if (!sessionStartTimeRef.current) {
      sessionStartTimeRef.current = new Date().toISOString();
    }
  };

  const appendTurn = (role: 'user' | 'assistant', content: string) => {
    conversationTurnsRef.current.push({
      timestamp: new Date().toISOString(),
      role,
      content,
    });
  };

  const markLastAssistantInterrupted = () => {
    const lastAssistantTurn = conversationTurnsRef.current.findLast((t) => t.role === 'assistant');
    if (lastAssistantTurn) {
      lastAssistantTurn.interrupted = true;
      console.log('Marked assistant turn as interrupted');
    }
  };

  const buildFinalizePayload = (): FinalizeTranscriptPayload => ({
    type: 'session.finalize',
    conversation_turns: conversationTurnsRef.current,
    start_time: sessionStartTimeRef.current,
  });

  const buildRestorePayload = (): RestoreTranscriptPayload => ({
    type: 'session.restore_transcript',
    conversation_turns: conversationTurnsRef.current,
    start_time: sessionStartTimeRef.current,
  });

  const reset = () => {
    conversationTurnsRef.current = [];
    sessionStartTimeRef.current = null;
  };

  return {
    conversationTurnsRef,
    sessionStartTimeRef,
    hasTurns,
    turnCount,
    ensureStartTime,
    appendTurn,
    markLastAssistantInterrupted,
    buildFinalizePayload,
    buildRestorePayload,
    reset,
  };
};
